import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { alertingActions } from '../+state/alerting.actions';
import { alertingFeature } from '../+state/alerting.reducer';
import {
  selectHasAlertByIsin,
  selectMaxPriceAlertsReached,
  selectNotifications,
  selectPriceAlertCount,
  selectTopics,
  selectUpdating,
} from '../+state/alerting.selectors';
import { PriceAlertDetails } from '../entities/price-alerts.entity';

@Injectable({ providedIn: 'root' })
export class AlertingFacade {
  readonly #store = inject(Store);

  public readonly topics$ = this.#store.select(selectTopics);
  public readonly notifications$ = this.#store.select(selectNotifications);
  public readonly priceAlertOverview$ = this.#store.select(alertingFeature.selectPriceAlertOverview);
  public readonly priceAlertDetails$ = this.#store.select(alertingFeature.selectSelectedPriceAlert);
  public readonly priceAlertDetailsQuote$ = this.#store.select(alertingFeature.selectSelectedPriceAlertQuote);
  public readonly priceAlertCount$ = this.#store.select(selectPriceAlertCount);
  public readonly updating$ = this.#store.select(selectUpdating);
  public readonly maxPriceAlertsReached$ = this.#store.select(selectMaxPriceAlertsReached);

  public hasAlerts(isin: string): Observable<boolean> {
    return this.#store.select(selectHasAlertByIsin(isin));
  }

  public loadNotifications(): void {
    this.#store.dispatch(alertingActions.loadNotifications());
  }

  public loadTopics(): void {
    this.#store.dispatch(alertingActions.loadTopics());
  }

  public setTopicSubscription(config: { topicName: string; status: boolean }): void {
    this.#store.dispatch(alertingActions.setTopicSubscription(config));
  }

  public loadPriceAlertOverview(): void {
    this.#store.dispatch(alertingActions.loadPriceAlertOverview());
  }

  public loadPriceAlertDetails(isin: string): void {
    this.#store.dispatch(alertingActions.loadPriceAlertDetails({ isin }));
  }

  public createPriceAlert(priceAlert: PriceAlertDetails, skipError = false): void {
    this.#store.dispatch(alertingActions.createPriceAlert({ priceAlert, skipError }));
  }

  public updatePriceAlertDetails(priceAlert: PriceAlertDetails): void {
    this.#store.dispatch(alertingActions.updatePriceAlertDetails({ priceAlert }));
  }

  public deletePriceAlert(isin: string): void {
    this.#store.dispatch(alertingActions.deletePriceAlert({ isin }));
  }

  public subscribeToPriceAlertDetailsQuote(isin: string): void {
    this.#store.dispatch(alertingActions.subscribeToPriceAlertDetailsQuote({ isin }));
  }

  public unsubscribeFromPriceAlertDetailsQuote(isin: string): void {
    this.#store.dispatch(alertingActions.unsubscribeFromPriceAlertDetailsQuote({ isin }));
  }

  public setVolaAlertActive(active: boolean): void {
    this.#store.dispatch(alertingActions.setVolaAlertActive({ active }));
  }

  public setQuoteAlertActive(active: boolean): void {
    this.#store.dispatch(alertingActions.setQuoteAlertActive({ active }));
  }
}
